
import type { PropType } from 'vue'
import { computed, defineComponent, ref } from 'vue'

// > components <
import Options from './Options.vue'

// > composable <
import useTooltip from '@/modern/composable/useTooltip'
import useClickOutside from '@/modern/composable/useClickOutside'

// > legacy/validation <
import useValidationMessages from '@/common/composable/useValidationMessages'
import type { ErrorObject } from '@vuelidate/core'

export default defineComponent({
    components: { Options },
    props: {
        size: {
            type: String as PropType<'medium' | 'big'>,
            default: 'medium'
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array as PropType<Array<Record<string, any>>>,
            default: () => []
        },
        modelValue: {
            type: [String, Number, null, Array] as PropType<string | number | null | Array<string | number>>,
            default: null
        },
        loading: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: null
        },
        displayBy: {
            type: String,
            default: 'name'
        },
        selectBy: {
            type: String,
            default: 'id'
        },
        closeAfterSelect: {
            type: Boolean,
            default: true
        },
        canDeselect: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        searchBy: {
            type: String,
            default: 'name'
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        validationErrors: {
            type: Array as PropType<ErrorObject[]>,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }) {
        const { getValidationMessage } = useValidationMessages()

        const query = ref<string | null>(null)
        const containerReference = ref<HTMLElement | null>(null)

        const { reference, tooltip, isVisible, x, y, strategy, open, close } = useTooltip({
            placement: 'bottom',
            strategy: 'absolute',
            offset: 4,
            autoPlacement: {
                allowedPlacements: ['bottom', 'top'],
            },
        })

        useClickOutside(containerReference, close)

        const closeOptions = () => {
            close()
            query.value = null
        }

        const toggleOptions = () => {
            if (props.disabled || props.loading) return

            if (isVisible.value) {
                closeOptions()
            } else {
                open()
            }
        }

        const onUpdate = (newValue: string | number | null | Array<string | number>) => {
            if (props.disabled) return

            emit('update:modelValue', newValue)
            emit('change', newValue)

            if (props.closeAfterSelect) {
                closeOptions()
            }
        }

        const selectedOption = computed(() => {
            if (Array.isArray(props.modelValue)) {
                if (props.modelValue.length === 1) {
                    return props.options.find((o) => o[props.selectBy] === (props.modelValue as Array<string | number>)[0])
                }

                return null
            }

            if (props.modelValue && props.options.length > 0) {
                return props.options.find((o) => o[props.selectBy] === props.modelValue)
            }

            return null
        })

        const filteredOptions = computed(() => {
            if (query.value) {
                const q = query.value.toLowerCase()

                return props.options.filter((option) => {
                    const value = option[props.searchBy]
                    if (value === undefined || value === null) return false
                    return String(value).toLowerCase().includes(q)
                })
            }

            return props.options
        })

        return {
            query,
            containerReference,
            reference,
            tooltip,
            isVisible,
            x,
            y,
            strategy,
            toggleOptions,
            onUpdate,
            selectedOption,
            filteredOptions,
            getValidationMessage
        }
    }
})
